import React, { useState, useMemo, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Table,
  Input,
  Label,
  UncontrolledTooltip,
  Row,
  Nav,
  NavItem,
  NavLink,
  Badge,
  TabContent,
  TabPane,
} from 'reactstrap';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import {
  MemberId,
  BillingName,
  Date,
  Total,
  PaymentStatus,
  PaymentMethod,
} from './MemberCol';
import { useRole } from 'hooks/useRole';
import { useAgeLimits } from 'hooks/useAgeLimits';
import { useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import TableContainer from '../../components/Common/TableContainer';
import { getTransactionApi } from 'api/transaction';
import {
  getMemberByIdApi,
  getNextOfKinByIdApi,
  getDependantsByIdApi,
  deleteDependent,
  editNextOfKin, updateMember, updateNextOfKin,
  getStatusOverride, submitStatusOverride, clearStatusOverride
} from 'api/member';
import {
  getAllRelationshipsApi,
  getClaimStatusCountsApi,
  getInvoicesCountsApi,
  getPaymentsStatusCountsApi,
} from 'api/report';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import StatCards from 'components/Common/StatCards';
import { formatDate, formatDateTime, ageInYears } from 'utils/dates';
import profileImg from '../../assets/images/profile-img.png';
import './member-details.scss';
import 'components/Common/tabs.scss';
import AddDependantModal from './AddDependantModal';
import AddNextOfKinModal from './AddNextOfKinModal';
import StatusOverrideModal from './StatusOverrideModal';
import Swal from 'sweetalert2';
import { allowedEmails } from 'configs/constants';
import { Tree, TreeNode } from 'react-organizational-chart';
import { DateField, SelectField } from 'components/Common/fields';
import notify from 'utils/notify';


/** Consistent pane header: title left, actions right, fixed height so panes align. */
const SectionHeader = ({ title, children }) => (
  <div className="section-header d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
    <h6 className="section-title mb-0">{title}</h6>
    <div className="d-flex flex-wrap gap-2">{children}</div>
  </div>
);

/** One label/value line in the profile card, with an optional pencil to edit. */
const ProfileRow = ({ label, children, onEdit, editing }) => (
  <li className="list-group-item d-flex align-items-start gap-3 px-4 py-3">
    <span className="text-muted flex-shrink-0" style={{ width: '8.5rem' }}>
      {label}
    </span>
    <span className="flex-grow-1 min-w-0 text-break">{children}</span>
    {onEdit && !editing && (
      <button
        type="button"
        className="btn btn-link p-0 text-muted flex-shrink-0"
        title={`Edit ${label.toLowerCase()}`}
        onClick={onEdit}
      >
        <i className="mdi mdi-pencil-outline font-size-16" />
      </button>
    )}
  </li>
);

const MemberDetails = () => {
  const dateFormat = 'DD-MM-YYYY';
  const queryClient = useQueryClient();
  const { isAdmin, isSuperAdmin, isData } = useRole();
  const { principalMinDob, maxDob } = useAgeLimits();
  const { id } = useParams();
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageNumber: 1,
    sortBy: 'id',
    ascendingSort: false,
    searchColumns: [
      { name: 'memberId', value: id },
      { name: 'endDate', value: moment().format(dateFormat) },
      {
        name: 'startDate',
        value: moment().subtract(2, 'year').format(dateFormat),
      },
    ],
  });

  const [dependent, setDependent] = useState({});
  const [paymentCount, setPaymentCounts] = useState([]);
  const [invoiceCount, setInvoiceCounts] = useState([]);
  const [claimsCounts, setClaimsCounts] = useState([]);
  const [showAddDep, setShowAddDeb] = useState(false);
  const [showAddNext, setShowAddNext] = useState(false);
  const [memberData, setMemberData] = useState({});
  const [nextOfKinData, setNextOfKinData] = useState({});
  const [dependantsData, setDependantsData] = useState([]);
  const [latestTransaction, setLatestTransaction] = useState([]);
  const [pages, setPages] = useState(0);
  const [hasEdit, setHasEdit] = useState({});
  const [showEditNextOfKin, setShowEditNextOfKin] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const [overrideInfo, setOverrideInfo] = useState(null);
  const [overriding, setOverriding] = useState(false);
  const overrideRoleId = parseInt(localStorage.getItem('shofco_role_id'), 10);
  const canOverride = isAdmin || isSuperAdmin;
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(1);
  const [dependantsViewMode, setDependantsViewMode] = useState('tree');
  const [nextOfKinForm, setNextOfKinForm] = useState({
    memberId: id,
    firstName: '',
    lastName: '',
    dob: moment(nextOfKinData.dob).format('DD-MM-YYYY'),
    gender: '',
    email: '',
    mobileNumber: '',
    relationship: '',
    identificationNumber: '',
  });

  const {
    mutate: mutateRelationships,
    isLoading: isLoadingRelationships,
    data: relationshipsData,
  } = useMutation((payload) => getAllRelationshipsApi(payload), {
    onSettled: () => {
      queryClient.invalidateQueries('/public/um/setup/relationship');
    },
  });
  const { mutate: mutatePaymentCounts, data: paymentCounts } = useMutation(
    (payload) => getPaymentsStatusCountsApi(payload),
    {
      onSuccess: (res) => {
        setPaymentCounts(res.data || []);
      },
      onSettled: () => {
        queryClient.invalidateQueries('/api/report/transaction/counts');
      },
    },
  );

  const { mutate: mutateClaimCounts, data: claimCounts } = useMutation(
    (payload) => getClaimStatusCountsApi(payload),
    {
      onSuccess: (res) => {
        setClaimsCounts(res.data || []);
      },
      onSettled: () => {
        queryClient.invalidateQueries('/api/report/claim/counts');
      },
    },
  );

  const { mutate: mutateInvoiceCounts, data: invoiceCounts } = useMutation(
    (payload) => getInvoicesCountsApi(payload),
    {
      onSuccess: (res) => {
        setInvoiceCounts(res.data || []);
      },
      onSettled: () => {
        queryClient.invalidateQueries('/api/report/transaction/invoices/counts');
      },
    },
  );

  useEffect(() => {
    if (nextOfKinData.length > 0) {
      const kin = nextOfKinData[0];
      setNextOfKinForm({
        memberId: id,
        firstName: kin.firstName,
        lastName: kin.lastName,
        dob: moment(kin.dob, 'DD-MM-YYYY').format('DD-MM-YYYY'),
        gender: kin.gender,
        email: kin.email,
        mobileNumber: kin.mobileNumber,
        relationship: kin.relationship,
        identificationNumber: kin.identificationNumber
      });
    }
  }, [nextOfKinData]);

  const toggleEditNextOfKinModal = () =>
    setShowEditNextOfKin(!showEditNextOfKin);

  const { mutate: submitEditNextOfKin, isLoading: isSubmitting } = useMutation(
    (isSuperAdmin || isAdmin || (isData && localStorage.getItem('shofco_email') === 'bawuor@shofco.org')) ? updateNextOfKin : editNextOfKin,
    {
      onSuccess: async () => {
        queryClient.invalidateQueries('/api/um/nextofkin/update');
        const nextOfKinResponse = await getNextOfKinByIdApi({
          memberId: id,
          status: 'Active',
        });
        setNextOfKinData(nextOfKinResponse);
        setShowEditNextOfKin(false);
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Request Succesful.',
          timer: 2000,
          showConfirmButton: false,
        });
      },
      onError: (error) => {
        console.error('Error editing next of kin:', error);
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: 'Something went wrong while updating the next of kin details.',
          footer: 'Please try again later.',
        });
      },
    },
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNextOfKinForm((prevForm) => ({ ...prevForm, [name]: value }));
  };

  const handleSubmit = () => {
    submitEditNextOfKin({ ...nextOfKinData[0], ...nextOfKinForm });
  };

  const fetchData = async () => {
    try {
      const memberResponse = await getMemberByIdApi(id);
      setHasEdit({
        ...hasEdit,
        firstName: { value: memberResponse.firstName },
        middleName: { value: memberResponse.middleName },
        lastName: { value: memberResponse.lastName },
      });
      setMemberData(memberResponse);
      getNextOfKinByIdApi({
        memberId: id,
        status: 'Active',
      }).then(res => {
        setNextOfKinData(res);
      });
      getDependantsByIdApi(id).then(res => {
        setDependantsData(res);
      });
      getTransactionApi({
        ...pagination,
        pageNumber: pagination.pageNumber - 1,
      }).then(res => {
        setLatestTransaction(res?.list);
        // page count for the transactions table (the API returns either a page
        // count or a total row count depending on the endpoint version)
        setPages(
          res?.pages ||
            res?.totalPages ||
            (res?.total ? Math.ceil(res.total / (pagination.pageSize || 10)) : 1),
        );
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    const dateRange = {
      fromDate: moment().subtract(1, 'year').format(dateFormat),
      toDate: moment().format(dateFormat),
      memberId: id,
      address: {},
    };
    fetchData();
    if (canOverride) {
      getStatusOverride(id).then(setOverrideInfo).catch(() => { });
    }
    mutateRelationships();
    mutateClaimCounts(dateRange);
    mutatePaymentCounts({
      ...pagination,
      fromDate:
        pagination.searchColumns?.find((c) => c.name === 'startDate')?.value ||
        moment().startOf('month').format(dateFormat),
      toDate:
        pagination.searchColumns.find((c) => c.name === 'endDate')?.value ||
        moment().format(dateFormat),
      memberId: id,
      customDate: true,
      address: {},
    });
    mutateInvoiceCounts(dateRange);
  }, [id, pagination]);

  document.title = 'Member Details | SUN Welfare Administration System';

  const columns = useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'dependentId',
        hidden: true,
        Cell: (cellProps) => <Total {...cellProps} />,
      },
      {
        Header: 'Full name',
        accessor: (row) => `${row.firstName || ''} ${row.lastName || ''}`,
        filterable: true,
        Cell: (cellProps) => <span className="fw-medium">{cellProps.value}</span>,
      },
      {
        Header: 'Relationship',
        accessor: 'relationship',
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => <BillingName {...cellProps} />,
      },
      {
        Header: 'Gender',
        accessor: 'gender',
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => <Date {...cellProps} />,
      },
      {
        Header: 'Date of birth',
        accessor: (row) => row?.dob,
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => <span>{formatDate(cellProps.value)}</span>,
      },
      {
        Header: 'Age',
        id: 'age',
        accessor: 'dob',
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => {
          const age = ageInYears(cellProps.value);
          return <span>{age === null ? '—' : `${age} yrs`}</span>;
        },
      },
      {
        Header: 'Birth Certificate No',
        accessor: 'birthCertificationNumber',
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => <Total {...cellProps} />,
      },
      {
        Header: 'Added on',
        accessor: 'insertDate',
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => <span>{formatDateTime(cellProps.value)}</span>,
      },
      {
        Header: 'Status',
        accessor: 'status',
        hidden: false,
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => <PaymentStatus {...cellProps} />,
      },
      {
        Header: 'Phone number',
        accessor: 'mobileNumber',
        disableFilters: true,
        Cell: (cellProps) => <span>{cellProps.value || '—'}</span>,
      },
    ],
    [],
  );

  const Transactioncolumns = useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'id',
        hidden: true,
        filterable: false,
        disableFilters: true,
        Cell: (cellProps) => <MemberId {...cellProps} />,
      },
      {
        Header: 'Transaction ID',
        accessor: 'transactionNumber',
        filterable: false,
        disableFilters: true,
        Cell: (cellProps) => <MemberId {...cellProps} />,
      },
      {
        Header: 'Amount',
        accessor: 'amount',
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => <BillingName {...cellProps} />,
      },
      {
        Header: 'Date',
        accessor: 'transactionDateTime',
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => <span>{formatDateTime(cellProps.value)}</span>,
      },
      {
        Header: 'Payment Status',
        accessor: 'status',
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => <PaymentStatus {...cellProps} />,
      },
      {
        Header: 'Reallocated Payment',
        accessor: (row) => {
          return row?.reAllocated
            ? `Reallocated from ${row.reAllocatedFromAccount} → ${row.account} on ${moment(row.reAllocatedAt).utcOffset('+0300').format('DD/MM/YYYY [at] h:mma')}` : 'NO';
        },
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => <BillingName {...cellProps} />,
      },
      {
        Header: 'Payment Method',
        accessor: 'paymentMode',
        disableFilters: true,
        Cell: (cellProps) => <PaymentMethod {...cellProps} />,
      },
    ],
    [],
  );

  const handleViewDependent = (record) => {
    console.log(record);
    if (record.dependentId) {
      navigate(`/receipts/${record.dependentId}`);
    } else {
      notify.error('Receipt Not Found', 'The receipt is missing or invalid.');
    }
  };

  const handleViewTransaction = (record) => {
    if (record.receiptId) {
      navigate(`/receipts/${record.receiptId}`);
    } else {
      notify.error('Receipt Not Found', 'The receipt is missing or invalid.');
    }
  };

  const handleShowAddNext = () => setShowAddNext(!showAddNext);
  const handleShowAddDep = () => {
    setDependent({});
    setShowAddDeb(true);
  };
  const editDependent = (data) => {
    setDependent(data);
    setShowAddDeb(true);
  };

  const totalPremiums = useMemo(
    () => paymentCount.reduce((acc, curr) => acc + curr.totalPremiums, 0),
    [paymentCount],
  );
  const totalInvoices = useMemo(
    () => invoiceCount.reduce((acc, curr) => acc + curr.totalInvoices, 0),
    [invoiceCount],
  );
  const totalClaims = useMemo(
    () => claimsCounts.reduce((acc, curr) => acc + curr.totalClaims, 0),
    [claimsCounts],
  );

  const formatToInputDate = (date) => {
    if (!date) return '';
    const [dd, mm, yyyy] = date.split('-');
    return `${yyyy}-${mm}-${dd}`;
  };

  const onEditMember = async (field) => {
    let value = memberData[field];

    if (field === 'dob') {
      value = formatToInputDate(memberData?.dob);
    }

    setHasEdit({
      ...hasEdit,
      [field]: { edit: true, value },
    });
  };

  const handleEdit = async (e) => {
    const name = e?.target?.name;
    const value = e?.target?.value;
    setHasEdit({ ...hasEdit, [name]: { edit: true, value } });
  };

  const handleDependent = async (d) => {
    await deleteDependent(d).then(() => {
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Deleted Succesfully.',
        timer: 2000,
        showConfirmButton: false,
      });
    });
  }

  const formatToBackendDate = (date) => {
    if (!date) return '';
    const [yyyy, mm, dd] = date.split('-');
    return `${dd}-${mm}-${yyyy}`;
  };

  const handleUpdateMember = async () => {
    try {
      const data = { ...memberData, notify: false };
      if (hasEdit?.firstName?.edit) data['firstName'] = hasEdit?.firstName?.value;
      if (hasEdit?.middleName?.edit) data['middleName'] = hasEdit?.middleName?.value;
      if (hasEdit?.lastName?.edit) data['lastName'] = hasEdit?.lastName?.value;
      if (hasEdit?.dob?.edit) data['dob'] = formatToBackendDate(hasEdit?.dob?.value);
      if (hasEdit?.email?.edit) data['email'] = hasEdit?.email?.value;
      if (hasEdit?.identificationNumber?.edit)
        data['identificationNumber'] = hasEdit?.identificationNumber?.value;
      if (hasEdit?.mobileNumber1?.edit)
        data['mobileNumber1'] = hasEdit?.mobileNumber1?.value;

      await updateMember(data)
        .then((res) => {
          notify.success('Member Updated', res.message);
          fetchData();
          setHasEdit({});
        })
        .catch((e) => {
          notify.error('Member Update Failed.', e.message || 'An error occurred during update');
        });
    } catch (err) {
      console.error('Error updating member:', err);
    }
  };

  const openOverride = async () => {
    if (!canOverride) {
      notify.warning('Not Authorized', 'Only Admin or Super Admin can override member status.');
      return;
    }
    try {
      const info = await getStatusOverride(id);
      setOverrideInfo(info);
      setShowOverride(true);
    } catch (e) {
      notify.error('Operation Failed.', e.message || 'Could not load status override.');
    }
  };

  const refreshOverrideInfo = async () => {
    try { setOverrideInfo(await getStatusOverride(id)); } catch (e) { /* non-fatal */ }
  };

  const handleSubmitOverride = async (payload) => {
    setOverriding(true);
    try {
      const res = await submitStatusOverride(id, payload);
      notify.success('Status Overridden', res.message);
      setShowOverride(false);
      fetchData();
      refreshOverrideInfo();
    } catch (e) {
      notify.error('Operation Failed.', e.message || 'An error occurred during the operation');
    } finally {
      setOverriding(false);
    }
  };

  const handleClearOverride = async () => {
    if (!canOverride) {
      notify.warning('Not Authorized', 'Only Admin or Super Admin can clear an override.');
      return;
    }
    setOverriding(true);
    try {
      const res = await clearStatusOverride(id, { reason: 'Override cleared' });
      notify.success('Override Cleared', res.message);
      fetchData();
      refreshOverrideInfo();
    } catch (e) {
      notify.error('Operation Failed.', e.message || 'An error occurred during the operation');
    } finally {
      setOverriding(false);
    }
  };

  const onChangePagination = ({ page, limit }, filters, sorter) => {
    let searchColumns = isEmpty(filters)
      ? pagination.searchColumns
      : [
        ...pagination.searchColumns,
        ...(filters?.id ? [{ name: 'id', value: filters?.id }] : []),
        ...(filters?.firstName ? [{ name: 'firstName', value: filters?.firstName }] : []),
        ...(filters?.lastName ? [{ name: 'lastName', value: filters?.lastName }] : []),
        ...(filters?.email ? [{ name: 'email', value: filters?.email }] : []),
        ...(filters?.transactionNumber
          ? [{ name: 'transactionNumber', value: filters?.transactionNumber }]
          : []),
        ...(filters?.idNumber ? [{ name: 'idNumber', value: filters?.idNumber }] : []),
        ...(filters?.mobileNumber1
          ? [{ name: 'mobileNumber1', value: filters?.mobileNumber1 }]
          : []),
      ];
    setPagination({
      ...pagination,
      pageNumber: page || pagination.pageNumber,
      pageSize: limit || pagination.pageSize,
      searchColumns: filters?.transactionNumber || filters?.idNumber
        ? searchColumns.filter((s) => s.name !== 'startDate' && s.name !== 'endDate')
        : searchColumns,
    });
  };

  const allowEdit = isAdmin || isSuperAdmin || isData || allowedEmails.includes(localStorage.getItem('shofco_email'));
  const allowKinEdit = isAdmin || isSuperAdmin || (localStorage.getItem('shofco_email') === 'bawuor@shofco.org');
  const kinNeedsApproval = !(isSuperAdmin || isAdmin || (isData && localStorage.getItem('shofco_email') === 'bawuor@shofco.org'));

  const StyledNode = ({ name, relationship, status }) => {
    const initials = String(name || '')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase();
    const active = String(status || '').toLowerCase() === 'active';
    return (
      <div className={`tree-node${relationship === 'Principal' ? ' tree-node--principal' : ''}`}>
        <span className="tree-node__avatar">{initials || '?'}</span>
        <span>
          <div className="tree-node__name">{name}</div>
          <div className="tree-node__meta">
            {relationship}
            <span
              className={`tree-node__dot bg-${active ? 'success' : 'danger'}`}
              title={status}
            />
            {status}
          </div>
        </span>
      </div>
    );
  };

  const familyTreeData = useMemo(() => {
    const principalNode = {
      label: (
        <StyledNode
          name={memberData.fullName || `${memberData.firstName || ''} ${memberData.lastName || ''}`}
          relationship="Principal"
          status={memberData.status || 'Active'}
        />
      ),
      children: [],
    };

    const spouse = dependantsData.find(
      (dep) => dep.relationship === 'Husband' || dep.relationship === 'Wife'
    );
    const children = dependantsData.filter(
      (dep) => dep.relationship === 'Son' || dep.relationship === 'Daughter'
    );

    if (spouse) {
      const spouseNode = {
        label: (
          <StyledNode
            name={`${spouse.firstName} ${spouse.lastName}`}
            relationship={spouse.relationship}
            status={spouse.status}
          />
        ),
        children: children.map((child) => ({
          label: (
            <StyledNode
              name={`${child.firstName} ${child.lastName}`}
              relationship={child.relationship}
              status={child.status}
            />
          ),
        })),
      };
      principalNode.children.push(spouseNode);
    } else {
      principalNode.children = children.map((child) => ({
        label: (
          <StyledNode
            name={`${child.firstName} ${child.lastName}`}
            relationship={child.relationship}
            status={child.status}
          />
        ),
      }));
    }

    return principalNode;
  }, [memberData, dependantsData]);

  const fullName = `${memberData?.firstName || ''} ${memberData?.middleName || ''} ${
    !memberData?.lastName || memberData?.lastName === 'NULL' ? '' : memberData.lastName
  }`
    .replace(/\s+/g, ' ')
    .trim();
  const initials = fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  const statusTone =
    memberData?.status === 'Active'
      ? 'success'
      : memberData?.status === 'Pending'
      ? 'warning'
      : 'danger';
  const location = memberData?.addresses?.[0]
    ? [
        memberData.addresses[0].wardName,
        memberData.addresses[0].constituencyName,
        memberData.addresses[0].countyName,
      ]
        .filter(Boolean)
        .join(', ')
    : '';
  const isEditing = (field) => !!(hasEdit?.[field]?.edit && allowEdit);
  const dirty =
    allowEdit && Object.values(hasEdit || {}).some((f) => f && f.edit);
  const cancelEdits = () =>
    setHasEdit({
      firstName: { value: memberData?.firstName },
      middleName: { value: memberData?.middleName },
      lastName: { value: memberData?.lastName },
    });
  const loadingMember = isEmpty(memberData);
  const Skel = ({ w = 6 }) => (
    <span className="placeholder-wave d-block">
      <span className={`placeholder col-${w} rounded`} />
    </span>
  );
  const nok = nextOfKinData?.[0];

  const memberStats = [
    { title: 'Invoices', description: totalInvoices, iconClass: 'bx-file' },
    {
      title: 'Total Payment',
      description: `KES ${Number(totalPremiums || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      iconClass: 'bx-dollar-circle',
    },
    { title: 'Claims', description: totalClaims, iconClass: 'bx-briefcase-alt' },
  ];

  const editField = (name, placeholder, extra = {}) => (
    <Input
      name={name}
      id={name}
      type="text"
      className="form-control form-control-sm"
      placeholder={placeholder}
      onChange={handleEdit}
      value={hasEdit?.[name]?.value || ''}
      {...extra}
    />
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Members" breadcrumbItem="Member Details" />

        {/* ---- header strip ---- */}
        <Card className="mb-3">
          <CardBody className="py-3">
            <div className="d-flex flex-wrap align-items-center gap-3">
              <span
                className={`rounded-circle bg-primary bg-opacity-10 text-primary fw-semibold d-inline-flex align-items-center justify-content-center flex-shrink-0${
                  loadingMember ? ' placeholder-wave' : ''
                }`}
                style={{ width: 48, height: 48, fontSize: 16 }}
              >
                {loadingMember ? '' : initials || '?'}
              </span>
              <div className="min-w-0 flex-grow-1" style={{ minHeight: 40 }}>
                {loadingMember ? (
                  <>
                    <Skel w={4} />
                    <Skel w={7} />
                  </>
                ) : (
                  <>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <h5 className="mb-0 fw-semibold text-truncate">{fullName || '—'}</h5>
                      {memberData?.status && (
                        <Badge className={`badge-soft-${statusTone} font-size-12`}>
                          {memberData.status}
                        </Badge>
                      )}
                    </div>
                    <div className="text-muted font-size-12 text-truncate">
                      {[memberData?.groupName, location].filter(Boolean).join(' · ')}
                    </div>
                  </>
                )}
              </div>
              <div className="d-flex flex-wrap gap-2 ms-md-auto">
                <Link to="/members" className="btn btn-light">
                  <i className="bx bx-arrow-back align-middle me-1" />
                  Back
                </Link>
                {canOverride && (
                  <Button type="button" color="secondary" outline onClick={openOverride}>
                    <i className="mdi mdi-shield-edit-outline align-middle me-1" />
                    Override Status
                  </Button>
                )}
                {isSuperAdmin && (
                  <Link to="edit" className="btn btn-success">
                    <i className="mdi mdi-pencil align-middle me-1" />
                    Edit
                  </Link>
                )}
              </div>
            </div>
          </CardBody>
        </Card>

        <Row>
          {/* ---- profile ---- */}
          <Col xl="4">
            <Card>
              <CardBody className="p-0">
                <div className="member-banner d-flex align-items-end justify-content-between px-4 pt-2">
                  <div className="pb-2">
                    <h6 className="mb-0 fw-semibold">SUN Welfare</h6>
                    <small className="opacity-75">Member profile</small>
                  </div>
                  <img src={profileImg} alt="" className="img-fluid" />
                </div>
                <div className="d-flex align-items-center justify-content-between px-4 pt-3 pb-2">
                  <h6 className="card-title mb-0">Profile</h6>
                  {/* fixed-height slot so the card doesn't jump when the buttons appear */}
                  <div className={`d-flex gap-2${dirty ? '' : ' invisible'}`} aria-hidden={!dirty}>
                    <Button size="sm" color="light" onClick={cancelEdits}>
                      Cancel
                    </Button>
                    <Button size="sm" color="primary" onClick={handleUpdateMember}>
                      <i className="mdi mdi-content-save-outline me-1" />
                      Save changes
                    </Button>
                  </div>
                </div>
                {loadingMember ? (
                  <ul className="list-group list-group-flush member-profile">
                    {['Full name', 'SHOFCO ID', 'ID number', 'Mobile', 'Date of birth', 'E-mail', 'Status', 'Subscription expiry', 'Group', 'Location'].map((l) => (
                      <ProfileRow key={l} label={l}>
                        <Skel w={l === 'Location' ? 9 : 5} />
                      </ProfileRow>
                    ))}
                  </ul>
                ) : (
                <ul className="list-group list-group-flush member-profile">
                  <ProfileRow
                    label="Full name"
                    editing={isEditing('firstName') || isEditing('lastName')}
                    onEdit={
                      allowEdit
                        ? () => {
                            onEditMember('firstName');
                            onEditMember('middleName');
                            onEditMember('lastName');
                          }
                        : undefined
                    }
                  >
                    {isEditing('firstName') || isEditing('lastName') ? (
                      <div className="d-grid gap-2">
                        {editField('firstName', 'First name')}
                        {editField('middleName', 'Middle name')}
                        {editField('lastName', 'Last name')}
                      </div>
                    ) : (
                      <span className="fw-medium">{fullName || '—'}</span>
                    )}
                  </ProfileRow>
                  <ProfileRow label="SHOFCO ID">{memberData?.shofcoId || '—'}</ProfileRow>
                  <ProfileRow
                    label="ID number"
                    editing={isEditing('identificationNumber')}
                    onEdit={allowEdit ? () => onEditMember('identificationNumber') : undefined}
                  >
                    {isEditing('identificationNumber')
                      ? editField('identificationNumber', 'Enter ID number')
                      : memberData?.identificationNumber || '—'}
                  </ProfileRow>
                  <ProfileRow
                    label="Mobile"
                    editing={isEditing('mobileNumber1')}
                    onEdit={allowEdit ? () => onEditMember('mobileNumber1') : undefined}
                  >
                    {isEditing('mobileNumber1')
                      ? editField('mobileNumber1', 'Enter primary mobile number')
                      : memberData?.mobileNumber1 || '—'}
                  </ProfileRow>
                  <ProfileRow
                    label="Date of birth"
                    editing={isEditing('dob')}
                    onEdit={allowEdit ? () => onEditMember('dob') : undefined}
                  >
                    {isEditing('dob') ? (
                      <DateField
                        name="dob"
                        id="dob"
                        min={principalMinDob}
                        max={maxDob}
                        onChange={handleEdit}
                        value={hasEdit?.dob?.value || ''}
                      />
                    ) : (
                      formatDate(memberData?.dob)
                    )}
                  </ProfileRow>
                  <ProfileRow
                    label="E-mail"
                    editing={isEditing('email')}
                    onEdit={allowEdit ? () => onEditMember('email') : undefined}
                  >
                    {isEditing('email')
                      ? editField('email', 'Enter e-mail', { type: 'email' })
                      : memberData?.email || '—'}
                  </ProfileRow>
                  <ProfileRow label="Status">
                    {memberData?.status ? (
                      <Badge className={`badge-soft-${statusTone} font-size-12`}>
                        {memberData.status}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </ProfileRow>
                  <ProfileRow label="Subscription expiry">
                    {formatDate(memberData?.currentSubscriptionEndDate)}
                  </ProfileRow>
                  <ProfileRow label="Group">{memberData?.groupName || '—'}</ProfileRow>
                  <ProfileRow label="Location">{location || '—'}</ProfileRow>
                </ul>
                )}
              </CardBody>
            </Card>

            {overrideInfo?.overrideLocked && (
              <Card className="border border-warning">
                <CardBody>
                  <div className="d-flex gap-2">
                    <i className="mdi mdi-alert-outline text-warning font-size-18" />
                    <div className="flex-grow-1">
                      <h6 className="mb-1">Status override active</h6>
                      <p className="text-muted mb-2 font-size-13">
                        {overrideInfo.overrideReason || '—'}
                        {overrideInfo.overrideComments ? ` — ${overrideInfo.overrideComments}` : ''}
                        {overrideInfo.overrideBy ? ` (by ${overrideInfo.overrideBy})` : ''}
                        {overrideInfo.overrideMode === 'STATUS_ONLY'
                          ? '. Status/maturity pinned; wallet still recalculating.'
                          : '. Recalculation paused until cleared.'}
                      </p>
                      {canOverride && (
                        <Button
                          size="sm"
                          color="warning"
                          onClick={handleClearOverride}
                          disabled={overriding}
                        >
                          Clear override
                        </Button>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}
          </Col>

          {/* ---- stats + tabs ---- */}
          <Col xl="8">
            <StatCards items={memberStats} md="4" />

            <Card>
              <CardBody>
                <Nav className="folder-tabs">
                  {[
                    { id: 1, label: 'Next of Kin', count: nextOfKinData?.length || 0 },
                    { id: 2, label: 'Dependants', count: dependantsData?.length || 0 },
                    { id: 3, label: 'Payments', count: latestTransaction?.length || 0 },
                  ].map((tab) => (
                    <NavItem key={tab.id}>
                      <NavLink
                        className={classnames({ active: activeTab === tab.id }, 'cursor-pointer')}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setActiveTab(tab.id)}
                      >
                        {tab.label}
                        <span
                          className={`badge ms-2 ${
                            activeTab === tab.id ? 'bg-white text-primary' : 'bg-light text-body'
                          }`}
                        >
                          {tab.count}
                        </span>
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>

                <TabContent activeTab={activeTab}>
                  {/* Next of Kin */}
                  <TabPane tabId={1}>
                    <SectionHeader title="Next of kin">
                      {allowKinEdit && nok && (
                        <Button size="sm" color="light" onClick={toggleEditNextOfKinModal}>
                          <i className="mdi mdi-pencil-outline me-1" />
                          Edit
                        </Button>
                      )}
                      {!nok && (
                        <Button size="sm" color="success" onClick={handleShowAddNext}>
                          <i className="mdi mdi-plus me-1" />
                          Add next of kin
                        </Button>
                      )}
                    </SectionHeader>
                    {nok ? (
                      <Row className="g-3 detail-grid">
                        {[
                          ['Full name', `${nok.firstName || ''} ${nok.lastName || ''}`.trim()],
                          ['Relationship', nok.relationship],
                          ['Mobile', nok.mobileNumber],
                          ['ID number', nok.identificationNumber],
                          ['E-mail', nok.email],
                          ['Status', nok.status],
                        ].map(([label, value]) => (
                          <Col sm="6" lg="4" key={label}>
                            <div className="detail-label">{label}</div>
                            <div className="detail-value">{value || '—'}</div>
                          </Col>
                        ))}
                      </Row>
                    ) : (
                      <p className="text-muted mb-0">No next of kin recorded.</p>
                    )}
                  </TabPane>

                  {/* Dependants */}
                  <TabPane tabId={2}>
                    <SectionHeader title="Dependants">
                        <div className="btn-group btn-group-sm" role="group">
                          <Button
                            color="light"
                            active={dependantsViewMode === 'list'}
                            onClick={() => setDependantsViewMode('list')}
                          >
                            <i className="mdi mdi-format-list-bulleted me-1" />
                            List
                          </Button>
                          <Button
                            color="light"
                            active={dependantsViewMode === 'tree'}
                            onClick={() => setDependantsViewMode('tree')}
                          >
                            <i className="mdi mdi-family-tree me-1" />
                            Family tree
                          </Button>
                        </div>
                        <Button size="sm" color="success" onClick={handleShowAddDep}>
                          <i className="mdi mdi-plus me-1" />
                          Add dependant
                        </Button>
                    </SectionHeader>
                    {dependantsViewMode === 'list' ? (
                      <TableContainer
                        columns={columns}
                        pages={1}
                        data={dependantsData || []}
                        isGlobalFilter={false}
                        isAddOptions={false}
                        isPagination={true}
                        iscustomPageSizeOptions={false}
                        customPageSize={4}
                        showEdit={allowEdit}
                        showDelete={allowKinEdit}
                        handleDelete={handleDependent}
                        handleEdit={editDependent}
                        rowClickable={allowEdit}
                        handleView={editDependent}
                        rowName={(r) => `${r.firstName || ''} ${r.lastName || ''}`.trim()}
                        paginationData={pagination}
                        onChangePagination={() => {}}
                        tableClass="align-middle table-nowrap"
                        theadClass="table-light"
                      />
                    ) : !isEmpty(dependantsData) ? (
                      <div className="family-tree">
                        <Tree
                          lineWidth={'2px'}
                          lineColor={'#c9d6dd'}
                          lineBorderRadius={'8px'}
                          label={familyTreeData.label}
                        >
                          {familyTreeData.children.map((node, index) => (
                            <TreeNode key={index} label={node.label}>
                              {node.children &&
                                node.children.map((child, childIndex) => (
                                  <TreeNode key={childIndex} label={child.label} />
                                ))}
                            </TreeNode>
                          ))}
                        </Tree>
                      </div>
                    ) : (
                      <p className="text-muted mb-0">No dependants recorded.</p>
                    )}
                  </TabPane>

                  {/* Payments */}
                  <TabPane tabId={3}>
                    <SectionHeader title="Latest transactions" />
                    <TableContainer
                      columns={Transactioncolumns}
                      pages={pages}
                      data={latestTransaction ? [...latestTransaction].reverse() : []}
                      isGlobalFilter={false}
                      isAddOptions={false}
                      isPagination={true}
                      iscustomPageSizeOptions={false}
                      customPageSize={4}
                      showView
                      rowClickable
                      handleView={handleViewTransaction}
                      paginationData={pagination}
                      onChangePagination={onChangePagination}
                      tableClass="align-middle table-nowrap"
                      theadClass="table-light"
                    />
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <StatusOverrideModal
          isOpen={showOverride}
          toggle={() => setShowOverride(!showOverride)}
          info={overrideInfo}
          onSubmit={handleSubmitOverride}
          submitting={overriding}
        />
        <AddDependantModal
          member={memberData}
          relationships={relationshipsData}
          isNew={dependent && dependent.dependentId ? false : true}
          initData={dependent}
          setVisible={() => setShowAddDeb(!showAddDep)}
          visible={showAddDep}
        />
        <AddNextOfKinModal
          member={memberData}
          relationships={relationshipsData}
          isNew={nextOfKinData && nextOfKinData.nextOfKinId ? false : true}
          initData={{}}
          setVisible={handleShowAddNext}
          visible={showAddNext}
        />
        <Modal isOpen={showEditNextOfKin} toggle={toggleEditNextOfKinModal}>
          <ModalHeader toggle={toggleEditNextOfKinModal}>Edit Next of Kin</ModalHeader>
          <ModalBody>
            <Form>
              <FormGroup>
                <Label for="firstName">First Name</Label>
                <Input
                  type="text"
                  name="firstName"
                  id="firstName"
                  value={nextOfKinForm.firstName}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label for="lastName">Last Name</Label>
                <Input
                  type="text"
                  name="lastName"
                  id="lastName"
                  value={nextOfKinForm.lastName}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label for="dob">Date of Birth</Label>
                <DateField
                  name="dob"
                  id="dob"
                  value={nextOfKinForm?.dob}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label for="gender">Gender</Label>
                <SelectField
                  name="gender"
                  id="gender"
                  value={nextOfKinForm.gender}
                  onChange={handleInputChange}
                >
                  <option>Male</option>
                  <option>Female</option>
                  <option>Intersex</option>
                  <option>Other</option>
                </SelectField>
              </FormGroup>
              <FormGroup>
                <Label for="email">Email</Label>
                <Input
                  type="email"
                  name="email"
                  id="email"
                  value={nextOfKinForm.email}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label for="mobileNumber">Mobile Number</Label>
                <Input
                  type="text"
                  name="mobileNumber"
                  id="mobileNumber"
                  value={nextOfKinForm.mobileNumber}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label for="relationship">Relationship</Label>
                <SelectField
                  name="relationship"
                  id="relationship"
                  value={nextOfKinForm.relationship}
                  onChange={handleInputChange}
                >
                  <option>Mother</option>
                  <option>Father</option>
                  <option>Wife</option>
                  <option>Husband</option>
                  <option>Son</option>
                  <option>Daughter</option>
                  <option>In-law</option>
                  <option>Brother</option>
                  <option>Sister</option>
                  <option>Uncle</option>
                  <option>Aunt</option>
                  <option>Grandparent</option>
                  <option>Cousin</option>
                  <option>Other</option>
                </SelectField>
              </FormGroup>
              <FormGroup>
                <Label for="identificationNumber">Identification Number</Label>
                <Input
                  type="number"
                  name="identificationNumber"
                  id="identificationNumber"
                  value={nextOfKinForm.identificationNumber}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : (kinNeedsApproval ? 'Submit for Approval' : 'Save Changes')}
            </Button>
            <Button color="secondary" onClick={toggleEditNextOfKinModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div >
  );
};

export default MemberDetails;
